import * as _ from "lodash";

export interface IdentifyableObject {
    /** must me convertible to number */
    id: string;
    $sortOrder: number;
}

interface Relation {
    firstId: string,
    secondId: string
}
export interface DataStoreExport {
    objects: Array<IdentifyableObject>;
    relations: {
        [type: string]: {
            [id: string]: number[];
        }
    };
}

export class DataStore {
    private idCounter = 1;
    private objects: { [id: string]: IdentifyableObject } = {};
    private relationsByType: { [type: string]: { [id: string]: number[] } } = {};
    private indexesByName: { [type: string]: number[] } = {};

    constructor(initialData?: DataStoreExport) {
        if (initialData) {
            initialData.objects.forEach(obj => {
                this.add(obj);
            });

            this.relationsByType = _.cloneDeep(initialData.relations);
        }
    }

    public add<T>(obj: T, indexName: string | null = null, cloneFn: (t: T) => T = _.cloneDeep): T & IdentifyableObject {
        var result = <T & IdentifyableObject>cloneFn(obj);
        result.id = this.getOrCreateId(result);
        result.$sortOrder = result.$sortOrder;

        if (this.objects[result.id]) {
            if (!_.isEqual(this.objects[result.id], result)) {
                throw Error("Different object with same Id already added");
            }
            return <T & IdentifyableObject>this.objects[result.id];
        }

        if (indexName) {
            getOrCreate(this.indexesByName, indexName, () => []).push(+result.id);
        }

        return this.objects[result.id] = result;
    }

    public get<T>(id: string | number): T & IdentifyableObject {
        return <T & IdentifyableObject>this.objects[id];
    }

    public getIndex<T>(name: string): Array<T & IdentifyableObject> {
        return this.indexesByName[name].map(id => this.get<T>(id));
    }

    public relateBothWays(first: IdentifyableObject, relationType: string, second: IdentifyableObject): void {
        this.addRelationIfNotContained(relationType, first, second);
        this.addRelationIfNotContained(relationType, second, first);
    }

    public relateOneToSome(single: IdentifyableObject, relationType: string, ...many: Array<IdentifyableObject>): void {
        many.forEach(oneOfMany => {
            this.addRelationIfNotContained(relationType, single, oneOfMany);
        });
    }

    private getOrCreateId(obj: IdentifyableObject): string {
        if (obj.id) {
            this.idCounter = Math.max(this.idCounter, +obj.id);
            return obj.id;
        } else {
            return "" + (++this.idCounter);
        }
    }

    private addRelationIfNotContained(relationType: string, first: IdentifyableObject, second: IdentifyableObject) {
        const relations = getOrCreate(this.relationsByType, relationType, () => { return {} });
        this.addRelation(relations, { firstId: first.id, secondId: second.id });
    }

    private addRelation(relations: { [id: string]: number[] }, relationToAdd: Relation) {
        const relationsOfFirst = getOrCreate<number[]>(relations, relationToAdd.firstId, () => []);
        if (!_.includes(relationsOfFirst, +relationToAdd.secondId)) {
            relationsOfFirst.push(+relationToAdd.secondId);
        }
    }

    public getManyRelated<TResult>(obj: IdentifyableObject, relationType: string): Array<TResult & IdentifyableObject> {
        const idList = this.relationsByType[relationType][obj.id];
        if (!idList) {
            return [];
        }
        return idList.map(id => this.get(id));
    }

    public getSingleRelated<TResult>(obj: IdentifyableObject, relationType: string): TResult & IdentifyableObject {
        const idList = this.relationsByType[relationType][obj.id];
        if (!idList) {
            throw new Error(`No relation of type ${relationType} found for object ${JSON.stringify(obj)}`);
        }
        return this.get(idList[0]);
    }

    public export(): DataStoreExport {
        this.clearSortOrderProperties();
        return {
            relations: _.cloneDeep(this.relationsByType),
            objects: <Array<IdentifyableObject>>_.cloneDeep(_.values(this.objects))
        }
    }

    public sortIndex<T>(name: string, comparer: (a: T, b: T) => number) {
        this.indexesByName[name].sort((a: number, b: number) => comparer(this.get<T>(a), this.get<T>(b)));
        this.indexesByName[name].forEach((id, index) => { this.get<T>(id).$sortOrder = index });
    }

    public clearSortOrderProperties() {
       // _.each(this.objects, o => { delete o.$sortOrder })
    }

    public sortRelation<T>(obj: IdentifyableObject, relationType: string, comparer: (a: T, b: T) => number) {
        const idList = this.relationsByType[relationType][obj.id] || [];
        idList.sort((a: number, b: number) => comparer(this.get<T>(a), this.get<T>(b)));
    }

    public sortRelations<T>(relationType: string, comparer: (a: T, b: T) => number) {
        const idLists = _.values(this.relationsByType[relationType]);
        idLists.forEach(idList => idList.sort((a: number, b: number) => comparer(this.get<T>(a), this.get<T>(b))));
    }
}

function getOrCreate<T>(map: { [name: string]: T }, name: string, create: () => T): T {
    const value = map[name];
    if (value) {
        return value;
    }
    else {
        return (map[name] = create());
    }
}


// WEBPACK FOOTER //
// ./src/providers/data/data-store.ts